/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useContext } from "react";
import { useHistory } from "react-router-dom";
import { useDispatch } from "react-redux";
import { Grid, MenuItem, TextField } from "@material-ui/core";
import { KeyboardDateTimePicker } from "@material-ui/pickers";
import EnhancedTable from "components/common_components/Tables/EnhancedTable";
import Button from "components/common_components/Button";
import Page from "components/common_components/Page/Page";
import Header from "components/common_components/Header";
import LoadingPage from "components/common_components/LoadingPage";
import Status from "components/common_components/Status";
import Icon from "components/common_components/Icon";
import SearchInput from "components/common_components/SearchInput/SearchInput";
import { notifyError, notifyInfo } from "redux/actions/Notifications";
import campaigns from "utils/static/campaigns";
import CampaignsContext from "./CampaignsContext";
import { buildCampaignFilter } from "./utils";

const headCells = [
    { index: 1, id: "name", label: "Name" },
    { index: 2, id: "startDatetimeString", orderBy: "startDatetime", label: "Starts" },
    { index: 3, id: "endDatetimeString", orderBy: "endDatetime", label: "Finishes" },
    {
        index: 4,
        id: "status",
        label: "Status",
        render: (_, { value }) => <Status id={value} withIcon />,
    },
    { index: 5, id: "stampCreationDatetimeString", orderBy: "stampCreationDatetime", label: "Created" },
];

const INITIAL_FILTERS = {
    quickFilterValue: "",
    startDatetimeFrom: null,
    startDatetimeTo: null,
    endDatetimeFrom: null,
    endDatetimeTo: null,
    status: "",
};

const dateTimePickerProps = {
    format: "MM/DD/YYYY HH:mm",
    fullWidth: true,
    size: "small",
    inputVariant: "outlined",
    margin: "dense",
    clearable: true,
    ampm: false,
    autoOk: true,
    KeyboardButtonProps: { size: "small" },
};

function List() {
    const history = useHistory();
    const dispatch = useDispatch();
    const { Campaigns, permissions } = useContext(CampaignsContext);

    const [isLoading, setIsLoading] = useState(false);
    const [data, setData] = useState([]);
    const [pageableData, setPageableData] = useState(undefined);
    const [filters, setFilters] = useState(INITIAL_FILTERS);

    function getCampaigns(requestData, showLoader = true) {
        if (showLoader) setIsLoading(true);
        Campaigns.getList({
            onSuccess: (list, pageable) => {
                setData(list);
                setPageableData(pageable);
                setIsLoading(false);
            },
            onInfo: (info, pageable) => {
                dispatch(notifyInfo(info));
                setData([]);
                setPageableData(pageable);
                setIsLoading(false);
            },
            onError: (error) => {
                dispatch(notifyError(error));
                setIsLoading(false);
            },
            data: requestData,
        });
    }

    useEffect(() => {
        getCampaigns();
        return () => setData([]);
    }, []);

    const filterBuilder = () => buildCampaignFilter(filters);

    const reload = (nextFilters = filters) => {
        getCampaigns({
            filter: buildCampaignFilter(nextFilters),
            orderBy: "stampCreationDatetime",
            orderDirection: "desc",
            pageNumber: 0,
            pageSize: 10,
        });
    };

    const onFilterChange = (key, value) => {
        const nextFilters = { ...filters, [key]: value };
        setFilters(nextFilters);
        // Date range and status filters apply immediately; the free-text quick
        // filter is applied through the search input's onSearch handler.
        if (key !== "quickFilterValue") reload(nextFilters);
    };

    const onSearch = () => reload();

    const onClearFilters = () => {
        setFilters(INITIAL_FILTERS);
        reload(INITIAL_FILTERS);
    };

    const onRowClickHandler = (item) => history.push(`/campaigns/${item.id}`);

    const onDeleteHandler = (selectedItems) => {
        Campaigns.deleteAll({
            ids: selectedItems,
            onSuccess: () => {
                dispatch(notifyInfo("Removed successfully!"));
                reload();
            },
            onError: (error) => dispatch(notifyError(error.message || error)),
        });
    };

    return (
        <Page className="campaigns-list-page">
            <Header title="Campaigns">
                {permissions.add && (
                    <Button primary onClick={() => history.push("/campaigns/add")}>
                        <Icon name="add" size="md" color="white" />
                        <span>Create new</span>
                    </Button>
                )}
            </Header>

            <div className="layout custom-filters">
                <Grid container spacing={2} alignItems="flex-end">
                    <Grid item xs={12} sm={6} md={3}>
                        <KeyboardDateTimePicker
                            {...dateTimePickerProps}
                            label="Starts from"
                            value={filters.startDatetimeFrom}
                            onChange={(date) => onFilterChange("startDatetimeFrom", date)}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <KeyboardDateTimePicker
                            {...dateTimePickerProps}
                            label="Starts to"
                            value={filters.startDatetimeTo}
                            onChange={(date) => onFilterChange("startDatetimeTo", date)}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <KeyboardDateTimePicker
                            {...dateTimePickerProps}
                            label="Finishes from"
                            value={filters.endDatetimeFrom}
                            onChange={(date) => onFilterChange("endDatetimeFrom", date)}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <KeyboardDateTimePicker
                            {...dateTimePickerProps}
                            label="Finishes to"
                            value={filters.endDatetimeTo}
                            onChange={(date) => onFilterChange("endDatetimeTo", date)}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <TextField
                            select
                            value={filters.status}
                            onChange={(e) => onFilterChange("status", e.target.value)}
                            label="Status"
                            fullWidth
                            size="small"
                        >
                            <MenuItem value="">Select...</MenuItem>
                            {campaigns.statuses.map((s) => (
                                <MenuItem key={s.value} value={s.value}>
                                    <Status id={s.value} withIcon />
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <SearchInput
                            value={filters.quickFilterValue}
                            onChange={(e) => setFilters((prev) => ({ ...prev, quickFilterValue: e.target.value }))}
                            onSearch={onSearch}
                            showLabel
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                        <Button link secondary onClick={onClearFilters}>
                            Clear filters
                        </Button>
                    </Grid>
                </Grid>
            </div>

            <div className="layout campaigns-table">
                {isLoading ? (
                    <LoadingPage />
                ) : (
                    <EnhancedTable
                        headCells={headCells}
                        data={data}
                        defaultOrder={{ order: "desc", orderBy: "stampCreationDatetimeString" }}
                        keyColumnName="id"
                        onRowClick={onRowClickHandler}
                        onDelete={onDeleteHandler}
                        onRequestData={getCampaigns}
                        filterBuilder={filterBuilder}
                        pageableData={pageableData}
                        defaultRowsPerPage={10}
                        filterable={false}
                        deletable={permissions.delete}
                        selectable={permissions.delete}
                    />
                )}
            </div>
        </Page>
    );
}

export default List;
